declare class WK {
  constructor(id: string);
  group: (group: string) => void;
  display: (display: Array<string>) => void;
  locale: (locale: string) => void;
  website: (website: string) => void;
  render: () => void;
}

declare global {
  interface Window {
    _paq?: Array<[string] | [string, string] | [string, string, Record<string, unknown>]>;
    dataLayer?: Array<Record<string, unknown>>;
    ma_track?: (...args: Array<string>) => void;
    fbq?: (event: string, value: string, params?: Record<string, string | number | undefined>) => void;
    welcomeKitReady?: (callback: () => void) => void;
    WelcomeKitEmbed?: new (id: string) => WK;
    pa?: {
      setUser: (userId: string) => void;
      deleteUser: () => void;
    };
  }
}

export type PageLoadEventInput = {
  // STANDARD
  page: string;
  page_chapter1?: string;
  page_chapter2?: string;
  page_chapter3?: string;
  // CUSTOM
  site_level2?: string;
};

export const pageLoadEvent = (input: PageLoadEventInput) => {
  window._paq = window._paq || [];
  window.dataLayer = window.dataLayer || [];
  // Base de l’event (toujours envoyé)
  // const baseEvent = {
  //   ...input,
  // };

  // Si opt-in → ajoute userEvent()
  // const finalEvent = mode === "opt-in" ? { ...userEvent(), ...baseEvent } : baseEvent; // en essential ou opt-out → pas de userEvent()

  // Envoi Piano
  // window._paq.push(["sendEvent", "page.display", finalEvent]);

  // Envoi GTM
  window.dataLayer.push({
    event: "pageLoad",
  });

  // Envoi Marketing Automation éventuel
  if (window.ma_track) {
    window.ma_track("aOcGfliaI");
  }
};

export const resultEvent = (rest: {
  ise_keyword?: string;
  ise_click_rank: number;
  ise_page: number;
}) => {
  window._paq = window._paq || [];

  window._paq.push(["sendEvent", "internal_search_result.click", rest]);
};

type ClickEventInput = {
  event: "click.action" | "click.navigation" | "click.download" | "click.exit";
  click: string;
  goal_type?: string;
  click_chapter1?: string;
  click_chapter2?: string;
  click_chapter3?: string;
  site_level2?: string;
} & ProductInput;
export const clickEvent = ({ event, ...rest }: ClickEventInput) => {
  window._paq = window._paq || [];

  window._paq.push(["sendEvent", event, rest]);
};

type ProductInput = {
  // STANDARD
  product?: string;
  product_id?: string;
  product_variant?: string;
  product_brand?: string;
  product_discount?: boolean;
  product_pricetaxincluded?: number;
  product_pricetaxfree?: number;
  product_currency?: "EUR";
  product_stock?: boolean;
  product_quantity?: number;
  product_category1?: string;
  product_category2?: string;
  product_category3?: string;
  product_category4?: string;
  product_cartcreation?: boolean;
  // CUSTOM
  speaker_ids?: Array<string>;
  registration_status?: string;
  travel_duration?: number;
  is_new_product_page?: boolean;
};

type TransactionInput = {
  transaction_promocode?: Array<string>;
  transaction_firstpurchase?: boolean;
};

type PaymentInput = {
  payment_mode?: string;
};

type ProductEventInput = {
  event: "product.display" | "product.page_display" | "product.add_to_cart" | "product.remove_from_cart";
  product_id: string;
} & ProductInput;

type ProductAwaitingPaymentEventInput = {
  event: "product.awaiting_payment";
  product_id: string;
  cart_id: string;
  cart_version: string;
} & ProductInput &
  TransactionInput &
  CartInput &
  PaymentInput;

type ProductPurchasedEventInput = {
  event: "product.purchased";
  product_id: string;
  cart_id: string;
  transaction_id: string;
} & ProductInput &
  TransactionInput &
  CartInput &
  PaymentInput;

export const productEvent = (
  input: ProductEventInput | ProductAwaitingPaymentEventInput | ProductPurchasedEventInput,
) => {
  window._paq = window._paq || [];

  const { event, ...rest } = input;
  window._paq.push(["sendEvent", event, rest]);
};

type CartInput = {
  // STANDARD
  cart_id?: string;
  cart_currency?: "EUR";
  cart_turnovertaxincluded?: number;
  cart_turnovertaxfree?: number;
  cart_creation_utc?: number;
  cart_quantity?: number;
  cart_nbdistinctproduct?: number;
};

type CartEventInput = {
  event: "cart.creation" | "cart.display" | "cart.update" | "cart.delivery" | "cart.payment";
  cart_id: string;
} & CartInput &
  ProductInput;

type CartAwaitingPaymentEventInput = {
  event: "cart.awaiting_payment";
  cart_id: string;
  cart_version: string;
} & CartInput &
  ProductInput &
  TransactionInput &
  PaymentInput;

export const cartEvent = (input: CartEventInput | CartAwaitingPaymentEventInput) => {
  window._paq = window._paq || [];

  const { event, ...rest } = input;
  window._paq.push(["sendEvent", event, rest]);
};

type SelfPromotionEventInput = {
  // STANDARD
  event: "self_promotion.display" | "self_promotion.click";
  onsitead_type: "Self promotion";
  onsitead_category?: string;
  onsitead_url?: string;
  // CUSTOM
  product_id?: string;
  departure_id?: string;
  onsitead_position?: number;
  product_list_id?: string;
  top_travel_list_id?: number;
};
export const selfPromotionEvent = ({ event, ...rest }: SelfPromotionEventInput) => {
  window._paq = window._paq || [];

  window._paq.push(["sendEvent", event, rest]);
};

type SearchEventInput = {
  // CUSTOM
  event: "search.query";
  search_term?: string;
  search_durations?: Array<string>;
  search_themes?: Array<string>;
  search_rythmes?: Array<string>;
  search_destinations?: Array<string>;
  search_speakers?: Array<string>;
  search_budgets?: Array<string>;
};

export const searchEvent = ({ event, ...rest }: SearchEventInput) => {
  window._paq = window._paq || [];

  window._paq.push(["sendEvent", event, rest]);
};

export const gtmEvent = (event: string, params?: Record<string, string | number | undefined>) => {
  window.dataLayer = window.dataLayer || [];

  window.dataLayer.push({
    event,
    ...params,
  });
};

// facebook
export const fbqEvent = (event: string, value: string, params?: Record<string, string | number | undefined>) => {
  window?.fbq?.(event, value, params);
};
